import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

export interface Employee {
  id: number;
  firstName: string;
  lastName: string;
}

export interface MyLeavesFilters {
  statusCheckboxes: Record<string, boolean>;
  startDateFilter: string;
  endDateFilter: string;
  requestedStartDate: string;
  requestedEndDate: string;
  leaveTypeFilter: string[];
  noOfDaysFilter: string;
  currentPage: number;
  pageSize: number;
  statusFilter: string;
  searchValue: string;
}

export interface MyLeaveBalancesFilters {
  leaveTypeFilter: string[];
  minBalanceFilter: string;
  maxBalanceFilter: string;
  yearFilter: string;
  currentPage: number;
  pageSize: number;
  searchValue: string;
}

export interface TeamLeavesFilters {
  statusCheckboxes: Record<string, boolean>;
  startDateFilter: string;
  endDateFilter: string;
  requestedStartDate: string;
  requestedEndDate: string;
  leaveTypeFilter: string[];
  noOfDaysFilter: string;
  employeeFilter: Employee | null;
  statusFilter: string;
  currentPage: number;
  pageSize: number;
  searchValue: string;
}

export interface TeamLeaveBalancesFilters {
  leaveTypeFilter: string[];
  minBalanceFilter: string;
  maxBalanceFilter: string;
  employeeFilter: Employee | null;
  yearFilter: string;
  currentPage: number;
  pageSize: number;
  searchValue: string;
}

export interface LeaveFiltersState {
  myLeaves: MyLeavesFilters;
  myLeaveBalances: MyLeaveBalancesFilters;
  teamLeaves: TeamLeavesFilters;
  teamLeaveBalances: TeamLeaveBalancesFilters;
  activeTab: string;
}

// ============================================================================
// Initial State
// ============================================================================

const defaultStatusCheckboxes = {
  Request: false,
  Approved: false,
  Availed: false,
  Cancel: false,
  Decline: false,
  CancelRequest: false
};

const initialState: LeaveFiltersState = {
  myLeaves: {
    statusCheckboxes: { ...defaultStatusCheckboxes },
    startDateFilter: '',
    endDateFilter: '',
    requestedStartDate: '',
    requestedEndDate: '',
    leaveTypeFilter: [],
    noOfDaysFilter: '',
    currentPage: 1,
    pageSize: 10,
    statusFilter: '',
    searchValue: ''
  },
  myLeaveBalances: {
    leaveTypeFilter: [],
    minBalanceFilter: '',
    maxBalanceFilter: '',
    yearFilter: new Date().getFullYear().toString(),
    currentPage: 1,
    pageSize: 10,
    searchValue: ''
  },
  teamLeaves: {
    statusCheckboxes: { ...defaultStatusCheckboxes },
    startDateFilter: '',
    endDateFilter: '',
    requestedStartDate: '',
    requestedEndDate: '',
    leaveTypeFilter: [],
    noOfDaysFilter: '',
    employeeFilter: null,
    statusFilter: 'All',
    currentPage: 1,
    pageSize: 10,
    searchValue: ''
  },
  teamLeaveBalances: {
    leaveTypeFilter: [],
    minBalanceFilter: '',
    maxBalanceFilter: '',
    employeeFilter: null,
    yearFilter: new Date().getFullYear().toString(),
    currentPage: 1,
    pageSize: 10,
    searchValue: ''
  },
  activeTab: ''
};

// ============================================================================
// Slice
// ============================================================================

const leaveFiltersSlice = createSlice({
  name: 'leaveFilters',
  initialState,
  reducers: {
    // My Leaves
    setMyLeavesFilters: (state, action: PayloadAction<Partial<MyLeavesFilters>>) => {
      state.myLeaves = { ...state.myLeaves, ...action.payload };
    },
    resetMyLeavesFilters: (state) => {
      state.myLeaves = initialState.myLeaves;
    },

    // My Leave Balances
    setMyLeaveBalancesFilters: (state, action: PayloadAction<Partial<MyLeaveBalancesFilters>>) => {
      state.myLeaveBalances = { ...state.myLeaveBalances, ...action.payload };
    },
    resetMyLeaveBalancesFilters: (state) => {
      state.myLeaveBalances = initialState.myLeaveBalances;
    },

    // Team Leaves
    setTeamLeavesFilters: (state, action: PayloadAction<Partial<TeamLeavesFilters>>) => {
      state.teamLeaves = { ...state.teamLeaves, ...action.payload };
    },
    resetTeamLeavesFilters: (state) => {
      state.teamLeaves = initialState.teamLeaves;
    },

    // Team Leave Balances
    setTeamLeaveBalancesFilters: (state, action: PayloadAction<Partial<TeamLeaveBalancesFilters>>) => {
      state.teamLeaveBalances = { ...state.teamLeaveBalances, ...action.payload };
    },
    resetTeamLeaveBalancesFilters: (state) => {
      state.teamLeaveBalances = initialState.teamLeaveBalances;
    },

    // Active Tab
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    // Reset all filters
    resetAllLeaveFilters: () => initialState
  }
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setMyLeavesFilters,
  resetMyLeavesFilters,
  setMyLeaveBalancesFilters,
  resetMyLeaveBalancesFilters,
  setTeamLeavesFilters,
  resetTeamLeavesFilters,
  setTeamLeaveBalancesFilters,
  resetTeamLeaveBalancesFilters,
  setActiveTab,
  resetAllLeaveFilters
} = leaveFiltersSlice.actions;

export default leaveFiltersSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectMyLeavesFilters = (state: { leaveFilters: LeaveFiltersState }) => state.leaveFilters.myLeaves;
export const selectMyLeaveBalancesFilters = (state: { leaveFilters: LeaveFiltersState }) => state.leaveFilters.myLeaveBalances;
export const selectTeamLeavesFilters = (state: { leaveFilters: LeaveFiltersState }) => state.leaveFilters.teamLeaves;
export const selectTeamLeaveBalancesFilters = (state: { leaveFilters: LeaveFiltersState }) => state.leaveFilters.teamLeaveBalances;
export const selectLeaveActiveTab = (state: { leaveFilters: LeaveFiltersState }) => state.leaveFilters.activeTab;
